// The candidate master screens' words (lane B3): the Candidates screen, Add candidate, Review parsed values and the
// Potential matches gate. Every entry quotes his glossary or the vendored design-system excerpts; the D0 label suites
// discover this module (a lane folder of src/views) and hold it to the same rules as src/mc. Words he wrote only in the
// BRD and that the vendored sources do not carry live in ./brd.ts, checked by tests/mc/b3-labels.test.mjs against the
// vendored BRD excerpt. Words the design system already defines (Potential matches, Use existing, Save, Proposed …)
// are imported from it, never redefined here.

import { defineLabels, template } from "@/mc/labels";

const MASTER_FIELDS =
  "\"Candidate ID, name, primary email, secondary email, phone, LinkedIn URL, resume versions, source, internal/external flag, talent-community status, consent/communication fields, and candidate history.\"";
const MANUAL_ENTRY = "\"Required manual-entry fields are name, primary email when available, source, and owner.\"";
const NO_EMAIL = "\"the system must support a documented no-email exception for sourced candidates.\"";
const METHODS =
  "\"V1 creation methods: resume upload with parsing and Recruiter review; manual candidate entry; and reuse of an existing talent-community or historical candidate profile.\"";
const POTENTIAL_MATCH =
  "\"Present potential matches before save and allow an authorized Recruiter or TA user to use the existing profile or create a distinct record with a documented reason.\"";
const Q_E15 = "*Edit profile* · *Link to requisition* · *Reuse an existing profile* · *Review parsed values*";
const PROFILE_STATUS = "\"Candidate profile Active/Archived/Merged\"";
const FILTERS =
  "| Filters | \"Support date, recruiter, coordinator, HRBP, HM, function, department, brand, job family, area of interest, level, location, strategy, new/replacement, status, phase, health, days open, time in stage, source, internal/external, offer status, standardized reasons, saved views, grouping, sorting, security-trimmed drill-down and exports.\" |";

/** Column heads, field names and the three creation methods. */
export const CANDIDATE_LABELS = /* @__PURE__ */ defineLabels({
  name: { text: "Name" },
  primaryEmail: { text: "Primary email" },
  secondaryEmail: { text: "Secondary email" },
  email: { text: "Email" },
  phone: { text: "Phone" },
  linkedinUrl: { text: "LinkedIn URL" },
  location: { text: "Location" },
  source: { text: "Source" },
  internalExternal: { text: "Internal/external" },
  internal: { text: "Internal" },
  external: { text: "External" },
  status: { text: "Status" },
  applications: { text: "Applications" },
  talentCommunity: { text: "Talent community" },
  owner: { text: "Owner" },
  resumeUpload: { text: "Resume upload" },
  manualCandidateEntry: { text: "Manual candidate entry" },
  reuseAnExistingProfile: { text: "Reuse an existing profile" },
  reviewParsedValues: { text: "Review parsed values" },
  noEmailException: { text: "No-email exception" },
  active: { text: "Active" },
  archived: { text: "Archived" },
  merged: { text: "Merged" },
  // the Candidates list's filter panel: the filters candidate.search takes (UAT D-12, CA-12)
  filters: { text: "Filters" },
});

/** His rule sentences: a blocked control's reason and a refusal's banner. */
export const CANDIDATE_RULES = /* @__PURE__ */ defineLabels({
  manualEntry: { text: "Required manual-entry fields are name, primary email when available, source, and owner." },
  noEmail: { text: "the system must support a documented no-email exception for sourced candidates." },
  humanConfirmation: { text: "Human confirmation before parsed values are authoritative." },
  potentialMatch: { text: "Present potential matches before save and allow an authorized Recruiter or TA user to use the existing profile or create a distinct record with a documented reason." },
  proposedForReview: { text: "Resume parsing must return proposed values for human review before save." },
  recruiterCreates: { text: "Recruiter creates/reviews profiles." },
});

// ---- the profile, Duplicate review and the Talent community screen (lane B3 S12) ------------------------------------
const TABS = "| DC-80 | C-3 | Tabs are his surfaces: Overview · Applications · Resume versions · Candidate timeline · Talent community (+ Internal mobility). | C1 | kept |";
const COMMUNITY = "\"Capture segment, tags, reason/date added, last contact, notes and prior requisition; do not auto-enroll in campaigns.\"";
const SOURCING = "\"Recruiter owns sourcing. Search prior finalists, alternates, silver medalists, internal talent and designated talent community members.\"";
const ADDS =
  "\"Recruiter intentionally adds eligible finalists, alternates, silver medalists and timing-related declines/withdrawals to talent community.\"";
const MOBILITY = "\"Capture current department/title/leader/location/grade and mobility type.\"";
const ATTESTATION = "\"Application includes self-attestation that current manager was informed; do not auto-notify manager.\"";
const MASTER = "\"One candidate master profile may be linked to multiple requisition-specific applications over time.\"";
const DUPLICATE = "| DC-85 | C-8 | The duplicate-review work item opens a *Duplicate review* page with the same grid. | C3, H1 | kept |";
const MERGE = "\"Support audited merge activity, preserve the original record history, and allow administrative recovery when a merge was performed incorrectly.\"";

const APPLICATION = "\"Each application retains its own stage, status, scorecards, communications, offer outcome, and disposition.\"";

/** The profile's tabs (DC-80), the talent-community and internal-mobility fields (BRD §9), and the search's sets. */
export const PROFILE_LABELS = /* @__PURE__ */ defineLabels({
  // His inspiration image opens the profile on Overview, the glossary's §2 word since D-23 (DC-80).
  overview: { text: "Overview" },
  internalMobility: { text: "Internal mobility" },
  // the rail's card: the one application the profile is read for now (D-8, CA-8)
  application: { text: "Application" },
  segment: { text: "Segment" },
  tags: { text: "Tags" },
  reasonDateAdded: { text: "Reason/date added" },
  lastContact: { text: "Last contact" },
  notes: { text: "Notes" },
  priorRequisition: { text: "Prior requisition" },
  priorFinalists: { text: "Prior finalists" },
  alternates: { text: "Alternates" },
  silverMedalists: { text: "Silver medalists" },
  internalTalent: { text: "Internal talent" },
  designatedMembers: { text: "Designated talent community members" },
  finalists: { text: "Finalists" },
  timingDeclines: { text: "Timing-related declines" },
  withdrawals: { text: "Withdrawals" },
  currentDepartment: { text: "Current department" },
  title: { text: "Title" },
  leader: { text: "Leader" },
  grade: { text: "Grade" },
  mobilityType: { text: "Mobility type" },
  selfAttestation: { text: "Self-attestation" },
  duplicateReview: { text: "Duplicate review" },
});

/** The hero's line under the name (D-8, CA-15): the current title at the current employer. */
export const PROFILE_TEMPLATES = {
  titleAtEmployer: /* @__PURE__ */ template({ text: "{title} at {employer}" }),
} as const;

/** The profile's rule sentences. */
export const PROFILE_RULES = /* @__PURE__ */ defineLabels({
  intentionallyAdds: { text: "Recruiter intentionally adds eligible finalists, alternates, silver medalists and timing-related declines/withdrawals to talent community." },
  attestation: { text: "Application includes self-attestation that current manager was informed; do not auto-notify manager." },
  mobilityCapture: { text: "Capture current department/title/leader/location/grade and mobility type." },
  mergeRecovery: { text: "Support audited merge activity, preserve the original record history, and allow administrative recovery when a merge was performed incorrectly." },
  materialUpdates: { text: "Material updates append history." },
  optimisticConcurrency: { text: "Use optimistic concurrency or equivalent for mutable workflow records." },
  closeWhenResolved: { text: "Deep link to exact screen; close automatically when resolved; retain history." },
});
